import { Box } from "../../components/styled-system/Box";
import { useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  uploadDatabase,
  uploadSynonyms,
  uploadLocations,
  downloadDatabase,
  downloadSynonyms,
  downloadLocations
} from "../../api/old-vine-registry-api";
import { authActions } from "../../store/auth/authSlice";
import { Paths } from "../../paths";
import { ErrorsTable } from "./errors/ErrorPage";
import { SubmissionsTable } from "./submissions/SubmissionPage";
import { SearchHistoryTable } from "./search-history/SearchHistory";
import { SealRegistrationsPanel } from "./seal-registrations/SealRegistrationsPanel";

const TABS = ["Update", "Seal Registrations", "Search History", "Submissions", "Errors"] as const;
type AdminTab = typeof TABS[number];

const UploadCompleted = (props: { uploadCompleted: boolean,
                                  uploadSuccess: boolean,
                                  uploadOption: string }) => (
    <>
      {props.uploadCompleted && (
          <span style={{paddingLeft:'20px', fontSize:"medium", color: props.uploadSuccess ? "black" : "black"}}>
            Upload Process Completed - please check error log for potential issues
          </span>
      )}
    </>
);

const Loading = (props: { loading: boolean }) => (
  <>
    {props.loading && (
      <Box display="inline-block" height="25px" ml="20px">
        {props.loading && <Spinner animation="border" role="status" />}
      </Box>
    )}
  </>
);

const AdminTabMenu = ({ activeTab, onTabChange }: { activeTab: AdminTab; onTabChange: (tab: AdminTab) => void }) => (
  <div style={{ display: "flex", gap: "0", borderBottom: "2px solid #ddd", marginBottom: "24px" }}>
    {TABS.map((tab) => (
      <button
        key={tab}
        onClick={() => onTabChange(tab)}
        style={{
          padding: "10px 20px",
          border: "none",
          borderBottom: activeTab === tab ? "3px solid #184472" : "3px solid transparent",
          background: "none",
          cursor: "pointer",
          fontFamily: "'Alegreya Sans', sans-serif",
          fontSize: "15px",
          fontWeight: activeTab === tab ? 700 : 500,
          color: activeTab === tab ? "#184472" : "#616161",
          marginBottom: "-2px",
        }}
      >
        {tab}
      </button>
    ))}
  </div>
);

export const AdminPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState<AdminTab>("Update");

  const handleLogout = () => {
    dispatch(authActions.setBearerToken(undefined));
    navigate(Paths.login);
  };

  const [uploadState, setUploadState] = useState({
    uploading: false,
    completed: false,
    success: false,
    option: "",
  });
  const [downloading, setDownloading] = useState(false);
  const [csvFile, setCsvFile] = useState<File>();
  const [error, setError] = useState("");
  const [vineyardValue, setVineyardValue] = useState('');
  const [synonymValue, setSynonymValue] = useState('');
  const [locationValue, setLocationValue] = useState('');

  const handleFileUpload = async (
    file: File | undefined,
    apiCall: (file: File) => Promise<void>,
    option: string
  ) => {
    if (file && validate(file)) {
      setUploadState({ uploading: true, completed: false, success: false, option });

      try {
        await apiCall(file);
        setUploadState({ uploading: false, completed: true, success: true, option });
        setCsvFile(undefined);
        if (option === "vineyard") setVineyardValue("");
        if (option === "synonym") setSynonymValue("");
        if (option === "location") setLocationValue("");
      } catch (error) {
        console.error("Upload failed:", error);
        setUploadState({ uploading: false, completed: true, success: false, option });
      }
    }
  };

  const uploadVineyardTsv = () => handleFileUpload(csvFile, uploadDatabase, "vineyard");
  const uploadSynonymTsv = () => handleFileUpload(csvFile, uploadSynonyms, "synonym");
  const uploadLocationTsv = () => handleFileUpload(csvFile, uploadLocations, "location");

  const validate = (file?: File) => {
    if (!file) {
      setError("TSV file required");
      return false;
    }
    if (!file.name.match(/.*\.[ct|CT][s|S][v|V]/)) {
      setError("File must be of type CSV or TSV");
      return false;
    }
    setError("");
    return true;
  };

  const handleVineyardsChange = (e:any) => {
    setVineyardValue(e.target.value);

    if (e.target.files && e.target.files.length > 0) {
      setCsvFile(e.target.files[0]);
      validate(e.target.files[0]);
    }
  };

  const handleSynonymChange = (e:any) => {
    setSynonymValue(e.target.value);

    if (e.target.files && e.target.files.length > 0) {
      setCsvFile(e.target.files[0]);
      validate(e.target.files[0]);
    }
  };

  const handleLocationChange = (e:any) => {
    setLocationValue(e.target.value);

    if (e.target.files && e.target.files.length > 0) {
      setCsvFile(e.target.files[0]);
      validate(e.target.files[0]);
    }
  };

  const handleDownload = async (downloadFn: () => Promise<void>) => {
    setDownloading(true);
    try {
      await downloadFn();
    } finally {
      setTimeout(() => setDownloading(false), 2000);
    }
  };


  const downloadVineyardsTsv = () => handleDownload(downloadDatabase);
  const downloadSynonymTsv = () => handleDownload(downloadSynonyms);
  const downloadLocationTsv = () => handleDownload(downloadLocations);

  return (
    <Box className="submit-layout" maxWidth={activeTab === "Update" ? "1100px" : "none"}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <div className="title-large">Administration</div>
        <button className="button" onClick={handleLogout}>
          Logout
        </button>
      </Box>

      <AdminTabMenu activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === "Update" && (
        <Box>
          <span className="title-small">Database update:</span>
          <br />
          <span className="description-text-center">
            To make updates to the vineyard database, please upload a TSV file here.
          </span>
          <br />
          {error && <span className="input-error">{error}</span>}
          <br />
          <Form.Control
            type="file"
            className="input-text"
            value={vineyardValue}
            onChange={handleVineyardsChange}
          />
          <span className="warning-text">
            WARNING: This is a destructive process - it will overwrite all existing data.
          </span>
          <br/>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button
              className="button u-mt-medium"
              onClick={uploadVineyardTsv}
              disabled={uploadState.uploading}
            >
              Upload Vineyards
            </button>
            <button className="button u-mt-medium" onClick={downloadVineyardsTsv} disabled={downloading} style={{ whiteSpace: "nowrap" }}>
              Download Vineyards
            </button>
          </div>
          {uploadState.option === "vineyard" && <>
                  <Loading loading={uploadState.uploading} />
                  <UploadCompleted
                        uploadOption={uploadState.option}
                        uploadCompleted={uploadState.completed}
                        uploadSuccess={uploadState.success} />
                  </>
          }
          <br/>
          <br />
          <br />
          <span className="description-text-center">
            To make updates to the synonym table, please upload a TSV file here.
          </span>
          <Form.Control
            type="file"
            className="input-text"
            value={synonymValue}
            onChange={handleSynonymChange}
          />
          <span className="warning-text">
            WARNING: This is a destructive process - it will overwrite all existing data.
          </span>
          <br/>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button
              className="button u-mt-medium"
              onClick={uploadSynonymTsv}
              disabled={uploadState.uploading}
            >
              Upload Synonyms
            </button>
            <button className="button u-mt-medium" onClick={downloadSynonymTsv} disabled={downloading} style={{ whiteSpace: "nowrap" }}>
              Download Synonyms
            </button>
          </div>
          {uploadState.option === "synonym" && <>
                  <Loading loading={uploadState.uploading} />
                  <UploadCompleted
                        uploadOption={uploadState.option}
                        uploadCompleted={uploadState.completed}
                        uploadSuccess={uploadState.success} />
                  </>
          }
          <br/>
          <br />
          <br />
          <span className="description-text-center">
            To make updates to the location table, please upload a TSV file here.
          </span>
          <Form.Control
            type="file"
            className="input-text"
            value={locationValue}
            onChange={handleLocationChange}
          />
          <span className="warning-text">
            WARNING: This is a destructive process - it will overwrite all existing data.
          </span>
          <br/>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button
              className="button u-mt-medium"
              onClick={uploadLocationTsv}
              disabled={uploadState.uploading}
            >
              Upload Locations
            </button>
            <button className="button u-mt-medium" onClick={downloadLocationTsv} disabled={downloading} style={{ whiteSpace: "nowrap" }}>
              Download Locations
            </button>
          </div>
          {uploadState.option === "location" && <>
                  <Loading loading={uploadState.uploading} />
                  <UploadCompleted
                        uploadOption={uploadState.option}
                        uploadCompleted={uploadState.completed}
                        uploadSuccess={uploadState.success} />
                  </>
          }
        </Box>
      )}

      {activeTab === "Seal Registrations" && <SealRegistrationsPanel />}
      {activeTab === "Search History" && <SearchHistoryTable />}
      {activeTab === "Submissions" && <SubmissionsTable />}
      {activeTab === "Errors" && <ErrorsTable />}
    </Box>
  );
};
